import React, { PointerEvent, ReactNode, useEffect, useRef, useState } from 'react';

import cls from 'classnames';

import IconCross from '@ott/icon-cross';

import { composeEventHandlers } from './helpers/composeEventHandlers';
import { Formatter, Parser } from './helpers/formatter';
import { getMaskText } from './helpers/getMaskText';
import { useInput } from './hooks/useInput';
import { useMergedRef } from './hooks/useMergedRef';

import styles from './Input.scss';

export const locator = {
  root: 'input',
  mask: 'input-mask',
  control: 'input-control',
} as const;

export type InputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'name' | 'value' | 'placeholder' | 'disabled' | 'size' | 'onChange'
> & {
  /** Дополнительные классы для потомков */
  childrenClassNames?: {
    label?: string;
    placeholder?: string;
    input?: string;
    postfixWrapper?: string;
  };

  /** Размер поля ввода */
  size?: 'default' | 'small';

  /** Имя поля ввода */
  name?: string;

  /** Значение поля ввода */
  value?: string;

  /** Текст для анимированного label, который не исчезает при вводе текста */
  label?: string;

  /** Текст для placeholder, который игнорируется, если установлен label */
  placeholder?: string;

  /** Определяет выводить ли крестик очистки */
  isClearable?: boolean;

  /** Используется вместо нативного disabled */
  isDisabled?: boolean;

  /** Определяет находится ли поле ввода в состоянии ошибки */
  hasError?: boolean;

  /** Маска для заполнения. Пример: 'ДД.ММ.ГГГГ' */
  mask?: string;

  /** Функция для форматирования ввода. Нужна для форматирования значения в поле ввода */
  formatter?: Formatter;

  /** Функция для парсинга вывода. Нужна для преобразования значения перед onChange */
  parser?: Parser;

  /** Элемент, который вставляется в конец поля ввода перед кнопкой очистки */
  postfix?: ReactNode;

  /** Кастомные data-locator'ы для тестирования */
  specificLocators?: string | string[];

  /** Возвращает значение введеных в поле ввода данных */
  onChange?: (value: string) => void;
};

const InputComponent = React.forwardRef<HTMLInputElement, InputProps>((props, forwardedRef) => {
  const {
    className,
    childrenClassNames = {},

    style,

    size = 'default',

    name,
    value = '',

    label,
    placeholder,

    isClearable,
    isDisabled,

    hasError = false,

    mask = '',
    formatter,
    parser,

    postfix,

    specificLocators,

    onChange,
    onFocus,
    onBlur,

    ...forwardedProps
  }: InputProps = props;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const inputMergedRef = useMergedRef(forwardedRef, inputRef);

  const [isFocused, setIsFocused] = useState(false);

  // Обработка autofocus:
  useEffect(() => {
    if (inputRef.current === document.activeElement) {
      setIsFocused(true);
    }
  }, [inputRef]);

  const [inputValue, handleInputChange] = useInput({
    ref: inputRef,
    externalValue: value,
    isFocused,
    formatter,
    parser,
    onChange,
  });

  const isSizeDefault = size === 'default';
  const isSizeSmall = size === 'small';

  const isFilled = Boolean(inputValue);

  const hasMask = Boolean(mask);
  const shouldShowMask = isSizeDefault && hasMask;

  const hasLabel = Boolean(label);
  const shouldShowLabel = isSizeDefault && hasLabel;

  const hasPlaceholder = Boolean(placeholder);
  const shouldShowPlaceholder = hasPlaceholder && !shouldShowLabel && !shouldShowMask && !isFilled;

  const shouldShowPostfix = Boolean(postfix);
  const shouldShowClearButton = isClearable && isFilled;

  const handleFocus = () => {
    setIsFocused(true);
  };
  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleClearButtonPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    // Предотвращаем потерю фокуса, чтобы кнопка очистики работала
    // в случае, когда проп isClearable завязан на onFocus / onBlur:
    event.preventDefault();
  };
  const handleClearButtonClick = () => {
    onChange?.('');

    inputRef.current?.focus();
  };

  return (
    <label
      className={cls(className, {
        [styles.root]: true,

        [styles.rootSizeDefault]: isSizeDefault,
        [styles.rootSizeSmall]: isSizeSmall,

        [styles.rootWithLabel]: shouldShowLabel,
        [styles.rootWithPostfix]: shouldShowPostfix,
        [styles.rootWithClearButton]: shouldShowClearButton,

        [styles.rootFilled]: isFilled,
        [styles.rootFocused]: isFocused,
        [styles.rootDisabled]: isDisabled,

        [styles.rootHasError]: !isDisabled && hasError,
      })}
      style={style}
      data-locator={cls(locator.root, specificLocators)}
    >
      <div className={styles.inputWrapper}>
        <div className={cls(styles.fadeGradient, styles.fadeGradientLeft)} />

        {shouldShowLabel && (
          <div className={cls(styles.label, childrenClassNames.label)}>{label}</div>
        )}

        {shouldShowPlaceholder && (
          <div className={cls(styles.placeholder, childrenClassNames.placeholder)}>
            {placeholder}
          </div>
        )}

        {shouldShowMask && (
          <input
            className={cls({
              [styles.inputMask]: true,
              [styles.inputMaskVisible]: isFocused || isFilled,
            })}
            value={getMaskText(inputValue, mask)}
            readOnly={true}
            tabIndex={-1}
            aria-hidden={true}
            data-locator={locator.mask}
          />
        )}

        <input
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...forwardedProps}
          ref={inputMergedRef}
          className={cls(styles.inputControl, childrenClassNames.input)}
          name={name}
          value={inputValue}
          disabled={isDisabled}
          data-error-type={hasError ? 'new' : null}
          data-error-placement={hasError ? 'top' : null}
          data-error={hasError}
          data-locator={locator.control}
          onChange={handleInputChange}
          onFocus={composeEventHandlers(handleFocus, onFocus)}
          onBlur={composeEventHandlers(handleBlur, onBlur)}
        />

        <div className={cls(styles.fadeGradient, styles.fadeGradientRight)} />
      </div>

      {(shouldShowPostfix || shouldShowClearButton) && (
        <div
          className={cls(styles.postfixAndClearButtonWrapper, childrenClassNames.postfixWrapper)}
        >
          {shouldShowPostfix && postfix}

          {shouldShowClearButton && (
            <button
              className={styles.clearButton}
              type="button"
              disabled={isDisabled}
              tabIndex={-1}
              aria-hidden={true}
              onPointerDown={handleClearButtonPointerDown}
              onClick={handleClearButtonClick}
            >
              <IconCross />
            </button>
          )}
        </div>
      )}
    </label>
  );
});

export const Input = React.memo(InputComponent);
